import { forwardRef, useContext, useEffect, useRef } from 'react';
import classNames from 'classnames';
import RcCheckbox from 'rc-checkbox';
import { GroupContext } from '../Group';
import { Root } from './style';
import { Props } from './types';

const InternalCheckbox: React.ForwardRefRenderFunction<HTMLInputElement, Props> = (
  {
    prefixCls = 'checkbox',
    className,
    children,
    indeterminate = false,
    style,
    onMouseEnter,
    onMouseLeave,
    skipGroup = false,
    palette,
    ...restProps
  },
  ref,
) => {
  const checkboxGroup = useContext(GroupContext);
  const prevValue = useRef(restProps.value);

  useEffect(() => {
    checkboxGroup?.registerValue(restProps.value);
  }, [checkboxGroup, restProps.value]);

  useEffect(() => {
    if (skipGroup) {
      return;
    }
    if (restProps.value !== prevValue.current) {
      checkboxGroup?.cancelValue(prevValue.current);
      checkboxGroup?.registerValue(restProps.value);
    }
    // eslint-disable-next-line consistent-return
    return () => checkboxGroup?.cancelValue(restProps.value);
  }, [checkboxGroup, restProps.value, skipGroup]);

  const checkboxProps: Props = { ...restProps };

  if (checkboxGroup && !skipGroup) {
    checkboxProps.onChange = (...args) => {
      if (restProps.onChange) {
        restProps.onChange(...args);
      }
      if (checkboxGroup.toggleOption) {
        checkboxGroup.toggleOption({ label: children, value: restProps.value });
      }
    };
    checkboxProps.name = checkboxGroup.name;
    checkboxProps.checked = checkboxGroup.value.indexOf(restProps.value) !== -1;
    checkboxProps.disabled = restProps.disabled || checkboxGroup.disabled;
  }

  const classString = classNames(
    {
      [`${prefixCls}-wrapper`]: true,
      [`${prefixCls}-wrapper-checked`]: checkboxProps.checked,
      [`${prefixCls}-wrapper-disabled`]: checkboxProps.disabled,
    },
    className,
  );

  const checkboxClass = classNames({
    [`${prefixCls}-indeterminate`]: indeterminate,
  });

  return (
    // eslint-disable-next-line jsx-a11y/label-has-associated-control
    <Root
      palette={palette}
      className={classString}
      style={style}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <RcCheckbox
        {...checkboxProps}
        prefixCls={prefixCls}
        className={checkboxClass}
        // @ts-ignore
        ref={ref}
      />
      {children}
    </Root>
  );
};

// @ts-ignore
const Checkbox = forwardRef<unknown, Props>(InternalCheckbox);

Checkbox.displayName = 'Checkbox';

export default Checkbox;
